import React, { FC, useEffect, useMemo, useState } from 'react'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'

import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'

import { LangValue } from '../../../../states/types/common'

import WorkingTime from '../../../components/risk/WorkingTime'
import UserData from '../../../components/risk/UserData'
import BizPointLineChart from '../../../components/risk/BizPointLineChart'
import WorkingDetail from '../../../components/risk/WorkDetail'

import {
  updateCurrentDate, updateCurrentEndDate, updateCurrentMonth, updateCurrentReportRangeType, updateCurrentStartDate,
} from '../../../../states/containers/common/currentSlice'
import {
  fetchReportTeam,
  fetchReportWorkingTime,
  fetchReportUserData,
  fetchReportLineChart,
  fetchReportWorkDetail,
  fetchTeamReportExists,
  resetState,
} from '../../../../states/containers/team/dailyReportSlice'
import {
  fetchReportTeam as fetchMonthlyReportTeam,
  fetchReportWorkingTime as fetchMonthlyReportWorkingTime,
  fetchReportUserData as fetchMonthlyReportUserData,
  fetchReportLineChart as fetchMonthlyReportLineChart,
  fetchReportWorkDetail as fetchMonthlyReportWorkDetail,
  fetchTeamReportExists as fetchMonthlyTeamReportExists,
  fetchReportHeatMap as fetchMonthlyReportHeatMap,
  resetState as resetMonthlyState,
} from '../../../../states/containers/team/monthlyReportSlice'
import {
  fetchReportTeam as fetchCustomReportTeam,
  fetchReportWorkingTime as fetchCustomReportWorkingTime,
  fetchReportUserData as fetchCustomReportUserData,
  fetchReportLineChart as fetchCustomReportLineChart,
  fetchReportWorkDetail as fetchCustomReportWorkDetail,
  fetchTeamReportExists as fetchCustomTeamReportExists,
  fetchReportHeatMap as fetchCustomReportHeatMap,
  resetState as resetCustomState,
} from '../../../../states/containers/team/customReportSlice'

import { endOfMonth, format, nowDate, startOfMonth } from '../../../../utils/date'
import { getGetPrams } from '../../../../utils/url'

import { URL } from '../../../constants/url'

import { createIntroPage } from '../../../../states/containers/common/introPagesSlice'
import TeamDailyIntro from '../../../components/risk/TeamDailyIntro'
import TeamMonthlyIntro from '../../../components/risk/TeamMonthlyIntro'
import { hasRead } from '../../../../utils/introPage'
import { mapAllowedDate } from '../../../../utils/plan'
import Loading from '../../../components/common/Loading'
import RangeBlock from '../../../components/risk/RangeBlock'
import TeamContentsHeader from '../../../components/risk/TeamContentsHeader'
import BizPointHeatMap from '../../../components/risk/BizPointHeatMap'
import TeamSummaryModule from '../../../components/task/TeamSummaryModule'
import { fetchTaskReportTeam } from '../../../../states/containers/task/taskSlice'
import PdfModule from '../../../components/common/PdfModule'

interface GetParams {
  target_date: string
  target_month: string
  target_start_date: string
  target_end_date: string
  type: string
}

const Detail: FC = () => {
  const {
    commonPart,
    current,
    dailyTeamReport,
    monthlyTeamReport,
    customTeamReport,
    task,
  } = useSelector((state: RootState) => (state))

  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { t } = useTranslation()

  const { id } = useParams<{ id: string | undefined }>()
  const { language } = useParams<{ language: LangValue }>()

  const [introShow, setIntroShow] = useState(false)

  const [selectRange, setSelectRange] = useState<string | null>("")

  const [isLoadAction, setIsLoadAction] = useState<boolean>(false)

  const [isDisablePdf, setIsDisablePdf] = useState<boolean>(true)

  const pdfTitle = useMemo(() => {
    let rangeText = ""
    if (selectRange === "daily") {
      rangeText = format(current.targetDate, "YYYY年MM月DD日")
    } else if (selectRange === "monthly") {
      rangeText = format(`${current.targetMonth}01`, "YYYY年MM月")
    } else {
      rangeText = `${format(current.targetStartDate, "YYYY年MM月DD日")} ~ ${format(current.targetEndDate, "YYYY年MM月DD日")}`
    }
    return `Qaseeアセスメントチームレポート ${commonPart.teams?.find((val) => val.id === Number(id))?.name} ${rangeText}`
  }, [selectRange, id, current])

  const updateData = (rangeType: string | null) => {
    if (!commonPart.company || !rangeType) return
    if (rangeType === 'daily') {
      const requestParam = {
        team_id: Number(id),
        target_date: current.targetDate,
      }

      dispatch(fetchReportTeam({ team_id: Number(id) }))
      dispatch(fetchReportWorkingTime(requestParam))
      dispatch(fetchReportUserData(requestParam))
      dispatch(fetchReportLineChart(requestParam))
      dispatch(fetchReportWorkDetail(requestParam))
      dispatch(fetchTaskReportTeam({
        id: id || "0",
        start_date: current.targetDate,
        end_date: current.targetDate,
      }))
    } else if (rangeType === 'monthly') {
      const requestParam = {
        team_id: Number(id),
        target_month: current.targetMonth,
      }

      dispatch(fetchMonthlyReportTeam({ team_id: Number(id) }))
      dispatch(fetchMonthlyTeamReportExists(requestParam))
      dispatch(fetchMonthlyReportWorkingTime(requestParam))
      dispatch(fetchMonthlyReportUserData(requestParam))
      dispatch(fetchMonthlyReportLineChart(requestParam))
      dispatch(fetchMonthlyReportWorkDetail(requestParam))
      dispatch(fetchMonthlyReportHeatMap(requestParam))
      dispatch(fetchTaskReportTeam({
        id: id || "0",
        start_date: startOfMonth(`${current.targetMonth}01`, "YYYY-MM-DD"),
        end_date: endOfMonth(`${current.targetMonth}01`, "YYYY-MM-DD"),
      }))
    } else if (rangeType === 'custom') {
      const requestParam = {
        team_id: Number(id),
        start_date: current.targetStartDate,
        end_date: current.targetEndDate,
      }

      dispatch(fetchCustomReportTeam({ team_id: Number(id) }))
      dispatch(fetchCustomTeamReportExists(requestParam))
      dispatch(fetchCustomReportWorkingTime(requestParam))
      dispatch(fetchCustomReportUserData(requestParam))
      dispatch(fetchCustomReportLineChart(requestParam))
      dispatch(fetchCustomReportWorkDetail(requestParam))
      dispatch(fetchCustomReportHeatMap(requestParam))
      dispatch(fetchTaskReportTeam({
        id: id || "0",
        start_date: current.targetStartDate,
        end_date: current.targetEndDate,
      }))
    }
  }

  useEffect(() => {
    if (!commonPart.company) return
    const searchParams = location.search
    const getParams = getGetPrams(searchParams) as GetParams
    const type = getParams.type || current.reportRangeType || "daily"
    dispatch(updateCurrentReportRangeType(type))
    setSelectRange(type)
    const targetDateParam = getParams.target_date || current.targetDate
    dispatch(updateCurrentDate(mapAllowedDate(targetDateParam, commonPart.company.plan, commonPart.company.created_at)))
    const targetMonthParam = getParams.target_month || current.targetMonth
    dispatch(updateCurrentMonth(targetMonthParam))
    const targetStartDateParam = getParams.target_start_date || format(current.targetStartDate, 'YYYY-MM-DD')
    dispatch(updateCurrentStartDate(targetStartDateParam))
    const targetEndDateParam = getParams.target_end_date || format(current.targetEndDate, 'YYYY-MM-DD')
    dispatch(updateCurrentEndDate(targetEndDateParam))
    setIsLoadAction(true)
  }, [location])

  useEffect(() => {
    if (!id || !isLoadAction) return
    if (current.reportRangeType === 'daily') {
      dispatch(resetState())
      dispatch(fetchTeamReportExists({
        team_id: Number(id),
        target_date: current.targetDate,
      }))
    }
    if (current.reportRangeType === 'monthly') {
      dispatch(resetMonthlyState())
      dispatch(fetchMonthlyTeamReportExists({
        team_id: Number(id),
        target_month: current.targetMonth,
      }))
    }
    if (current.reportRangeType === 'custom') {
      dispatch(resetCustomState())
      dispatch(fetchCustomTeamReportExists({
        team_id: Number(id),
        start_date: current.targetStartDate,
        end_date: current.targetEndDate,
      }))
    }
    setIsLoadAction(false)
  }, [isLoadAction])

  useEffect(() => {
    if (dailyTeamReport.evaluationExists) {
      updateData(selectRange)
    }
  }, [dailyTeamReport.evaluationExists])

  useEffect(() => {
    if (monthlyTeamReport.evaluationExists) {
      updateData(selectRange)
    }
  }, [monthlyTeamReport.evaluationExists])

  useEffect(() => {
    if (customTeamReport.evaluationExists) {
      updateData(selectRange)
    }
  }, [customTeamReport.evaluationExists])

  useEffect(() => {
    if (!commonPart.read_first_intro_ids || language === 'en') return
    const showIntro = !hasRead(URL.RISK_TEAM_DETAIL.path, commonPart.read_first_intro_ids)
    setIntroShow(showIntro)
  }, [commonPart])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangeDate = (value: any) => {
    history.push(`?type=daily&target_date=${format(value, 'YYYY-MM-DD')}`)
  }

  const handleChangeMonth = (value: number) => {
    history.push(`?type=monthly&target_month=${value}`)
  }

  const handleChangeRange = (value: {startDate: string, endDate: string}) => {
    if (value.startDate !== value.endDate) {
      history.push(`?type=custom&target_start_date=${value.startDate}&target_end_date=${value.endDate}`)
    }
  }

  const handleOpenIntroModal = () => {
    setIntroShow(true)
  }

  const handleCloseIntroModal = () => {
    setIntroShow(false)
    dispatch(
      createIntroPage(
        URL.RISK_TEAM_DETAIL.path,
        () => { /* do nothing */ },
      ),
    )
  }

  const renderContent = () => (
    <>
      <WorkingTime
        isWorkingTimeLoading={dailyTeamReport.isWorkingTimeLoading}
        date={current.targetDate}
        workingTime={dailyTeamReport.workingTime}
        mode="daily"
      />
      <UserData
        isLoading={dailyTeamReport.isUserDataLoading}
        userData={dailyTeamReport.userData}
        mode="daily"
        isTeamManager={
          commonPart.team_manager_ids && dailyTeamReport.team && commonPart.team_manager_ids.includes(dailyTeamReport.team.id)
        }
      />
      <BizPointLineChart
        isLoading={dailyTeamReport.isLineChartDataLoading}
        lineChartData={dailyTeamReport.lineChartData}
      />
      <WorkingDetail
        isWorkDetailLoading={dailyTeamReport.isWorkDetailLoading}
        workDetail={dailyTeamReport.workDetail}
      />
      <div style={{ marginLeft: "0.5em", marginRight: "0.5em" }}>
        <div className="box summary">
          <TeamSummaryModule
            members={task.taskReportTeam?.members}
          />
        </div>
      </div>
    </>
  )

  const renderNoContent = () => (
    format(current.targetDate) === nowDate() ? (
      <>
        <div style={{ paddingTop: "4em", textAlign: "center" }}>
          <span style={{ fontSize: "32px", color: "#094E8D", fontWeight: "bold" }}>{t('comment.today.comment_1_2')}</span>
        </div>
        <div style={{ paddingTop: "0.25em", textAlign: "center" }}>
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>{t('comment.today.comment_2')}</span>
        </div>
      </>
    ) : (
      <>
        <div style={{ paddingTop: "4em", textAlign: "center" }}>
          <span style={{ fontSize: "32px", color: "#094E8D", fontWeight: "bold" }}>{t(`team.report.no_data.${current.reportRangeType}.title`)}</span>
        </div>
        <div style={{ paddingTop: "0.25em", textAlign: "center" }}>
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>{t('team.report.no_data.description')}</span>
        </div>
        <div style={{ paddingTop: "1.5em", textAlign: "center" }}>
          <p style={{ fontSize: "12px", color: "rgba(51, 51, 51, 0.75)" }}>
            {t('team.report.no_data.contact_first')}
            <br />
            {t('team.report.no_data.contact_second')}
          </p>
        </div>
      </>
    )
  )

  const renderDailyBody = () => {
    if (!dailyTeamReport.evaluationExists) return renderNoContent()
    return renderContent()
  }

  const renderMonthlyBody = () => {
    if (!monthlyTeamReport.evaluationExists) return renderNoContent()
    return (
      <>
        <WorkingTime
          isWorkingTimeLoading={monthlyTeamReport.isWorkingTimeLoading}
          date={current.targetMonth}
          workingTime={monthlyTeamReport.workingTime}
          mode="monthly"
        />
        <UserData
          isLoading={monthlyTeamReport.isTeamLoading || monthlyTeamReport.isUserDataLoading}
          userData={monthlyTeamReport.userData}
          mode="monthly"
          isTeamManager={commonPart.team_manager_ids && monthlyTeamReport.team && commonPart.team_manager_ids.includes(monthlyTeamReport.team.id)}

        />
        <BizPointHeatMap
          isLoading={monthlyTeamReport.isHeatMapLoading}
          heatMapData={monthlyTeamReport.heatMap}
          month={current.targetMonth}
          isTeamManager={commonPart.team_manager_ids && monthlyTeamReport.team && commonPart.team_manager_ids.includes(monthlyTeamReport.team.id)}
        />
        <BizPointLineChart
          isLoading={monthlyTeamReport.isLineChartDataLoading}
          lineChartData={monthlyTeamReport.lineChartData}
        />
        <WorkingDetail
          isWorkDetailLoading={monthlyTeamReport.isWorkDetailLoading}
          workDetail={monthlyTeamReport.workDetail}
        />
        <div style={{ marginLeft: "0.5em", marginRight: "0.5em" }}>
          <div className="box summary">
            <TeamSummaryModule
              members={task.taskReportTeam?.members}
            />
          </div>
        </div>
      </>
    )
  }

  const renderCustomBody = () => {
    if (!customTeamReport.evaluationExists) return renderNoContent()
    return (
      <>
        <WorkingTime
          isWorkingTimeLoading={customTeamReport.isWorkingTimeLoading}
          date={{ start_date: current.targetStartDate, end_date: current.targetEndDate }}
          workingTime={customTeamReport.workingTime}
          mode="custom"
        />
        <UserData
          isLoading={customTeamReport.isTeamLoading || customTeamReport.isUserDataLoading}
          userData={customTeamReport.userData}
          mode="custom"
          isTeamManager={commonPart.team_manager_ids && customTeamReport.team && commonPart.team_manager_ids.includes(customTeamReport.team.id)}

        />
        <BizPointHeatMap
          isLoading={customTeamReport.isHeatMapLoading}
          heatMapData={customTeamReport.heatMap}
          isTeamManager={commonPart.team_manager_ids && customTeamReport.team && commonPart.team_manager_ids.includes(customTeamReport.team.id)}
        />
        <BizPointLineChart
          isLoading={customTeamReport.isLineChartDataLoading}
          lineChartData={customTeamReport.lineChartData}
        />
        <WorkingDetail
          isWorkDetailLoading={customTeamReport.isWorkDetailLoading}
          workDetail={customTeamReport.workDetail}
        />
        <div style={{ marginLeft: "0.5em", marginRight: "0.5em" }}>
          <div className="box summary">
            <TeamSummaryModule
              members={task.taskReportTeam?.members}
            />
          </div>
        </div>
      </>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderBody = ():any => {
    if (selectRange === "daily") {
      return (
        <>
          {
            // eslint-disable-next-line max-len
            dailyTeamReport.isDataExistsLoading
              || dailyTeamReport.evaluationExists === undefined
              || dailyTeamReport.userProcessExists === undefined
              || dailyTeamReport.isDisApprove === undefined
              ? (
                <Loading />
              ) : (
                renderDailyBody()
              )
          }
        </>
      )
    }
    if (selectRange === "monthly") {
      return (
        <>
          {
            // eslint-disable-next-line max-len
            monthlyTeamReport.isDataExistsLoading
              || monthlyTeamReport.evaluationExists === undefined
              || monthlyTeamReport.userProcessExists === undefined
              || monthlyTeamReport.isDisApprove === undefined
              ? (
                <Loading />
              ) : (
                renderMonthlyBody()
              )
          }
        </>
      )
    }
    return (
      <>
        {
          // eslint-disable-next-line max-len
          customTeamReport.isDataExistsLoading
            || customTeamReport.evaluationExists === undefined
            || customTeamReport.userProcessExists === undefined
            || customTeamReport.isDisApprove === undefined
            ? (
              <Loading />
            ) : (
              renderCustomBody()
            )
        }
      </>
    )
  }

  useEffect(() => {
    if (
      (selectRange === "daily" && dailyTeamReport.isDataExistsLoading)
      || (selectRange === "monthly" && monthlyTeamReport.isDataExistsLoading)
      || (selectRange === "custom" && customTeamReport.isDataExistsLoading)
      || task.isLoading
    ) {
      setIsDisablePdf(true)
      return
    }
    setTimeout(() => {
      setIsDisablePdf(false)
    }, 5000)
  }, [dailyTeamReport, monthlyTeamReport, customTeamReport, task])

  return (
    <section className="risk_page">
      <Helmet>
        <title>Qasee - リスクアセスメント - チームレポート</title>
      </Helmet>
      <section className="breadcrumb">
        リスクアセスメント
        &nbsp;&gt;&nbsp;
        チームレポート
        &nbsp;
      </section>
      <section className="page_header">
        <h1 className="question">
          チームレポート
          <span className="period">{t(`module.${current.reportRangeType}`)}</span>
          &nbsp;
          {
            language === 'ja' && (
              <div className="icon_question" onClick={handleOpenIntroModal} role="none">
                <i className="fas fa-question-circle" />
              </div>
            )
          }
        </h1>
        <RangeBlock
          changeDateCallback={handleChangeDate}
          changeMonthCallback={handleChangeMonth}
          changeRangeCallback={handleChangeRange}
        />
      </section>
      <section className="page_header">
        <div />
        <div className="tools">
          <PdfModule
            title={pdfTitle}
            target="pdf_area"
            disabled={isDisablePdf}
          />
        </div>
      </section>
      <section id="pdf_area">
        <section className="page_contents content-area clear">
          <section className="content">
            <div className="row">
              <TeamContentsHeader />
              {renderBody()}
            </div>
          </section>
        </section>
        {
          current.reportRangeType === 'daily' ? (
            <TeamDailyIntro isOpen={introShow} onCloseModal={handleCloseIntroModal} />
          ) : (
            <TeamMonthlyIntro isOpen={introShow} onCloseModal={handleCloseIntroModal} />
          )
        }
      </section>
    </section>
  )
}

export default Detail
